import { useDispatch, useSelector } from 'react-redux'
import { SmileOutlined } from '@ant-design/icons'
import { Modal, Collapse } from 'antd'
import { selectFlags } from '@Builder/QuoteDetailPage/selectors'
import { HTS_TAB_DETAILS } from '@Orders/constants'
import styles from '@Orders/OrderDetailsPage/styles.css'
import { AnyAction } from 'redux'

import Box from '~/core/Box'
import Button from '~/core/Button'
import Text from '~/core/Text'
import Link from '~/core/Link'
import {
    openChatDriftHTSCodes,
    openChatDriftForm5106,
} from '~/redux-modules/drift'
import getColor from '~/lib/getColor'
import { SALES_EMAIL } from '~/lib/emailAndUrls'
import { mailtoLink } from '~/lib/mailtoLink'
import { getenv } from '~/settings'
import LearnMoreLink from '~/core/LearnMoreLink/LearnMoreLink'

const LINK_TO_INTERNATIONAL_SHIPPING_REQUIREMENTS = `${
    getenv('REGIONAL_CONFIGS')?.urls
        ?.guide_to_international_shipping_requirements
}`

export const MoreInformationText = (props: { openChat: () => AnyAction }) => {
    const { openChat } = props
    const dispatch = useDispatch()
    const { disableDriftMessaging } = useSelector(selectFlags)

    return (
        <Box display="block">
            <Box paddingTop="15px">
                <SmileOutlined
                    style={{ fontSize: '21px', color: getColor('link') }}
                />
                {disableDriftMessaging ? (
                    <Link
                        name="modal-btn"
                        style={{ paddingLeft: '8px' }}
                        placement={HTS_TAB_DETAILS}
                        href={mailtoLink(SALES_EMAIL, 'I need help!')}
                        target="_blank"
                        data-test-target="contact-sales"
                    >
                        Contact us
                    </Link>
                ) : (
                    <Link
                        name="modal-btn"
                        style={{ paddingLeft: '8px' }}
                        placement={HTS_TAB_DETAILS}
                        onClick={(event: Event) => {
                            event.preventDefault()
                            dispatch(openChat())
                        }}
                        data-test-target="contact-sales"
                    >
                        Contact us
                    </Link>
                )}{' '}
                <Text style={{ marginLeft: '4px' }}>for help</Text>
            </Box>
            <Box paddingTop="10px">
                <LearnMoreLink
                    name="modal-btn"
                    linkTo={LINK_TO_INTERNATIONAL_SHIPPING_REQUIREMENTS}
                    placement={HTS_TAB_DETAILS}
                    followingText="about international shipping requirements"
                />
            </Box>
        </Box>
    )
}

export const htsModalForPartDescription = (
    visible: boolean,
    onCancel: Function,
) => {
    const { Panel } = Collapse

    return (
        <Modal
            title={
                <Box display="block" className={styles.modalHeader}>
                    HTS codes and part descriptions
                </Box>
            }
            visible={visible}
            wrapClassName={styles.modalArea}
            footer={
                <Box className={styles.modalFooterForHtsCodeAndDescription}>
                    <Box display="block">
                        <MoreInformationText openChat={openChatDriftHTSCodes} />
                    </Box>
                    <Button
                        name="btnGotIt"
                        placement={HTS_TAB_DETAILS}
                        type="primary"
                        style={{ width: '120px' }}
                        onClick={() => onCancel()}
                    >
                        Ok, got it
                    </Button>
                </Box>
            }
            onCancel={() => onCancel()}
        >
            <Box display="block">
                <p>
                    Harmonized Tariff Schedule (HTS) codes and part descriptions
                    are used in international trade to keep track of all
                    imported goods and to estimate the appropriate duty for each
                    importation.
                </p>
                <p>
                    Customs and Border Protection requires this information be
                    provided for all shipments to the United States. Failure to
                    provide the requested information can cause the shipment to
                    be delayed upon arrival at the border.
                </p>
                <Collapse bordered={false} style={{ marginTop: 10 }}>
                    <Panel
                        key={1}
                        className={styles.panel}
                        header={
                            <Box display="block" className={styles.iconLink}>
                                Why can't Fictiv enter the information for me?
                            </Box>
                        }
                    >
                        We prepare the customs paperwork for you, but our EXW
                        Incoterms state that as the customer, you are the
                        importer of record. This means that it is your
                        responsibility to handle logistics related to
                        importation of your parts into the United States.
                    </Panel>
                </Collapse>
            </Box>
        </Modal>
    )
}

export const form5106Modal = (visible: boolean, onCancel: Function) => {
    return (
        <Modal
            title={
                <Box className={styles.modalHeader}>
                    Form 5106 (importer profile)
                </Box>
            }
            visible={visible}
            wrapClassName={styles.modalArea}
            footer={
                <Box className={styles.modalFooterForHtsCodeAndDescription}>
                    <Box display="block">
                        <MoreInformationText openChat={openChatDriftForm5106} />
                    </Box>
                    <Button
                        name="btnGotIt"
                        placement={HTS_TAB_DETAILS}
                        type="primary"
                        style={{ width: '120px' }}
                        onClick={() => onCancel()}
                    >
                        Ok, got it
                    </Button>
                </Box>
            }
            onCancel={() => onCancel()}
        >
            <p>
                Form 5106 is required by Customs and Border Protection to
                identify you or your business as an importer of record. You only
                need to file it once, when you or your business receive overseas
                orders from Fictiv the first time.
            </p>
            <p>
                We will show you how to file Form 5106 after you click "Get
                started".
            </p>
        </Modal>
    )
}
